import { useEffect, useRef, useState } from 'react';

interface UseParallaxOptions {
  speed?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  disabled?: boolean;
}

export const useParallax = (options: UseParallaxOptions = {}) => {
  const { speed = 0.5, direction = 'up', disabled = false } = options;
  const [offset, setOffset] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) return;

    const handleScroll = () => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const scrolled = window.pageYOffset;
      const rate = scrolled * -speed;

      setOffset(rate);
    };

    const rafId = requestAnimationFrame(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [speed, disabled]);

  const getTransform = () => {
    if (disabled) return '';
    
    switch (direction) {
      case 'up':
        return `translate3d(0, ${offset}px, 0)`;
      case 'down':
        return `translate3d(0, ${-offset}px, 0)`;
      case 'left':
        return `translate3d(${offset}px, 0, 0)`;
      case 'right':
        return `translate3d(${-offset}px, 0, 0)`;
      default:
        return `translate3d(0, ${offset}px, 0)`;
    }
  };

  return {
    ref,
    transform: getTransform(),
    style: { transform: getTransform() }
  };
};

// Hook para floating animation
export const useFloating = (duration: number = 3000) => {
  const [y, setY] = useState(0);

  useEffect(() => {
    let animationId: number;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = (elapsed % duration) / duration;
      const yOffset = Math.sin(progress * Math.PI * 2) * 8;
      
      setY(yOffset);
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [duration]);

  return {
    style: { 
      transform: `translate3d(0, ${y}px, 0)`,
      transition: 'transform 0.1s ease-out'
    }
  };
};